'use client';

import {
  useState, useEffect, useCallback, useRef
} from 'react';
// @ts-expect-error - No type definitions available
import camelCase from 'lodash.camelcase';
import { usePurchaseFlowRenderProps } from '@common/config/purchaseFlowRenderHelpers';
import { useGatewayScript } from './useGatewayScript';
import { usePurchaseLocale, usePurchaseLog } from '../../config/PurchaseFlowConfigProvider';
import { PURCHASE_FLOW_TEXT_KEYS } from '../../utils/constants';
import { OnLogEvent } from '../../types';

// ============================================================================
// Types
// ============================================================================

interface FlowTokenizeData {
  token: string;
  type: string;
  expiry_month: number;
  expiry_year: number;
  scheme: string;
  last4: string;
  bin: string;
}

interface FlowTokenizeResult {
  data: FlowTokenizeData;
  cardMetadata: Record<string, unknown>;
}

interface FlowCardComponent {
  isAvailable: () => Promise<void>;
  mount: (selector: string) => void;
  unmount: () => void;
  tokenize: () => Promise<FlowTokenizeResult>;
  // Flow's card component exposes isValid() on the instance passed into
  // onChange; typed optional because it's not always present during init.
  isValid?: () => boolean;
}

interface FlowCardBinMetadata {
  scheme?: string;
  card_type?: string;
  bin?: string;
}

interface FlowCheckout {
  create: (type: string, options: Record<string, unknown>) => FlowCardComponent;
}

interface FlowPaymentSessionConfig {
  id: string;
  _links?: Record<string, unknown>;
  payment_session_token?: string;
  payment_session_secret?: string;
}

export interface CheckoutConfig {
  publicKey: string;
  jsSrc?: string;
  paymentSession?: FlowPaymentSessionConfig;
  environment?: 'sandbox' | 'production';
  placeholders: {
    cardNumber: string;
    cvv: string;
    expiryMonth: string;
    expiryYear: string;
  };
  style?: Record<string, unknown>;
  textColor?: string;
  flowElementId?: string;
  validationErrors?: Record<string, string>;
}

export interface UseCheckoutOptions {
  config: CheckoutConfig;
  onValidationChange?: (errors: Record<string, string>) => void;
  onCardTypeChange?: (scheme: string) => void;
  log?: OnLogEvent;
}

export interface UseCheckoutReturn {
  isScriptReady: boolean;
  isSetupComplete: boolean;
  scriptError: Error | null;
  gatewayErrors: Record<string, string>;
  fieldFocus: string | null;
  cardType: string | null;
  isFlowMode: boolean;
  submitCard: (cardholderName: string, zip: string) => Promise<Record<string, unknown>>;
  isCardValid: () => boolean;
  validateAllFields: () => boolean;
  reset: () => void;
  clearGatewayError: (key: string) => void;
}

// ============================================================================
// Constants
// ============================================================================

const CHECKOUT_INPUT_TYPES = {
  cardNumber: 'ccn',
  cvv: 'cvv',
  expiryDate: 'exp'
} as const;

const CHECKOUT_FRAME_CLASSES: Record<string, string> = {
  cardNumber: 'card-number-frame',
  cvv: 'cvv-frame',
  expiryDate: 'expiry-date-frame'
};

const TOKEN_EXPIRED_ERROR = 'token expired';

const DEFAULT_TEXT_COLOR = '#121212';

const getDefaultCheckoutStyle = (textColor: string) => ({
  base: {
    fontSize: '16px',
    fontWeight: 700,
    height: '100%',
    width: '100%',
    backgroundColor: 'transparent',
    borderRadius: 0,
    padding: 0,
    color: textColor
  },
  autofill: { color: textColor },
  hover: { color: textColor },
  focus: { color: textColor },
  valid: { color: textColor },
  invalid: { color: textColor },
  placeholder: {
    base: {
      fontSize: '16px',
      fontWeight: 400
    }
  }
});

const mapFlowTokenizeResult = (result: FlowTokenizeResult): Record<string, unknown> => {
  const { data } = result;
  return {
    token: data.token,
    scheme: data.scheme,
    last4: data.last4,
    expiry_month: data.expiry_month,
    expiry_year: data.expiry_year,
    bin: data.bin,
    card_type: data.type
  };
};

// ============================================================================
// Hook
// ============================================================================

export const useCheckout = ({
  config,
  onValidationChange,
  onCardTypeChange,
  log: optionalLog
}: UseCheckoutOptions): UseCheckoutReturn => {
  const isFlowMode = !!config.paymentSession;

  const [setupComplete, setSetupComplete] = useState(false);
  const [gatewayErrors, setGatewayErrors] = useState<Record<string, string>>({});
  const [fieldFocus, setFieldFocus] = useState<string | null>(null);
  const [cardType, setCardType] = useState<string | null>(null);
  // Flow-only: set from the card component's onChange (selfComponent.isValid()).
  // Frames reports validity via FRAME_VALIDATION_CHANGED + window.Frames.isCardValid(),
  // so this stays false in Frames mode and is not read there.
  const [isFlowCardComplete, setIsFlowCardComplete] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const flowCardRef = useRef<FlowCardComponent | null>(null);

  const contextLog = usePurchaseLog();
  const log = optionalLog ?? contextLog;

  const locale = usePurchaseLocale();

  const { renderText } = usePurchaseFlowRenderProps();

  // ---------------------------------------------------------------------------
  // Script loading — Flow SDK or Frames depending on config
  // ---------------------------------------------------------------------------
  const { isReady: isScriptReady, error: scriptError } = useGatewayScript({
    globalVarName: isFlowMode ? 'CheckoutWebComponents' : 'Frames',
    scriptSrc: config.jsSrc,
    checkFunction: isFlowMode
      ? () => typeof window !== 'undefined' && !!window.CheckoutWebComponents
      : () => typeof window !== 'undefined' && !!window.Frames
  });

  // ---------------------------------------------------------------------------
  // Flow mode initialization
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isFlowMode || !isScriptReady || !window.CheckoutWebComponents || !config.publicKey || !config.paymentSession) {
      return () => {};
    }

    let cancelled = false;

    const initFlow = async () => {
      try {
        const ps = config.paymentSession!;

        // Align Flow's in-iframe UI (labels + input chrome) with the app's
        // native .label / .input styles. Flow runs in cross-origin iframes so
        // this is the only hook we have — our SCSS never reaches it. Resolve
        // the brand's CSS vars via getComputedStyle on the mount point so
        // per-brand overrides (KA/PS/PG/PO-MBA) carry through.
        const mountEl = typeof document !== 'undefined'
          ? document.getElementById(config.flowElementId)
          : null;
        const cs = mountEl ? window.getComputedStyle(mountEl) : null;
        const readVar = (name: string, fallback: string): string => {
          const v = cs?.getPropertyValue(name).trim();
          return v || fallback;
        };

        const inheritedFontFamily = cs?.fontFamily || undefined;
        const labelColor = config.textColor || DEFAULT_TEXT_COLOR;
        // Match native .input: border var(--app-border-primary-color),
        // background var(--app-background-secondary-color), focus
        // var(--app-blue-snap-input-border-focus-color, var(--app-color)),
        // error var(--app-blue-snap-input-error-color, #e74c3c),
        // radius var(--app-blue-snap-input-border-radius, 4px).
        const colorFormBorder = readVar('--app-border-primary-color', '#E5E5E5');
        const colorFormBackground = readVar('--app-background-secondary-color', '#FFFFFF');
        const colorFocus = readVar('--app-blue-snap-input-border-focus-color', '')
          || readVar('--app-color', '#5E48FC');
        const colorError = readVar('--app-blue-snap-input-error-color', '#E74C3C');
        const inputRadius = readVar('--app-blue-snap-input-border-radius', '4px');

        const appearance = {
          colorPrimary: labelColor,
          colorBorder: colorFormBorder,
          colorFormBorder,
          colorFormBackground,
          colorAction: colorFocus,
          colorOutline: '#00000000',
          colorError,
          // [small, large] — Flow uses the small radius for inputs and the
          // large one for the outer form wrapper. Our inputs share the same
          // radius token so use it for both.
          borderRadius: [inputRadius, inputRadius],
          label: {
            fontSize: '12px',
            fontWeight: 700,
            ...(inheritedFontFamily ? { fontFamily: inheritedFontFamily } : {})
          }
        };

        const translations = {
          [locale]: {
            'card.card_number.invalid': renderText(PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_CARD_NUMBER),
            'card.card_number.not_supported': renderText(PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_CARD_NUMBER),
            'card.expiry_date.invalid': renderText(PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_EXPIRY),
            'card.security_code.invalid': renderText(PURCHASE_FLOW_TEXT_KEYS.CC_ERROR_CVV),

            'card.card_number': renderText(PURCHASE_FLOW_TEXT_KEYS.CC_LABEL_CARD_NUMBER),
            'card.expiry_date': renderText(PURCHASE_FLOW_TEXT_KEYS.CC_LABEL_EXPIRY),
            'card.security_code': renderText(PURCHASE_FLOW_TEXT_KEYS.CC_LABEL_CVV)
          }
        };

        const checkout: FlowCheckout = await (window.CheckoutWebComponents as (opts: Record<string, unknown>) => Promise<FlowCheckout>)({
          paymentSession: ps,
          publicKey: config.publicKey,
          environment: config.environment,
          locale,
          appearance,
          translations
        });

        const cardComponent = checkout.create('card', {
          showPayButton: false,
          displayCardholderName: 'hidden',
          // Mirror Frames' READY / FRAME_VALIDATION_CHANGED / CARD_BIN_CHANGED
          // semantics so canSaveCard can react to card validity and scheme.
          onReady: () => {
            if (!cancelled) setSetupComplete(true);
          },
          onChange: (self: FlowCardComponent) => {
            if (cancelled) return;
            const valid = typeof self?.isValid === 'function' ? self.isValid() : false;
            setIsFlowCardComplete(!!valid);
          },
          onCardBinChanged: (_self: FlowCardComponent, metadata?: FlowCardBinMetadata) => {
            if (cancelled || !metadata) return;
            const scheme = metadata.scheme || null;
            setCardType(scheme);
            if (scheme) onCardTypeChange?.(scheme);
          },
          onError: (_self: FlowCardComponent, err: unknown) => {
            log('error', 'CC Checkout Flow', 'Flow component error', err);
          }
        });

        await cardComponent.isAvailable();

        if (cancelled) return;

        cardComponent.mount(`#${ config.flowElementId }`);
        flowCardRef.current = cardComponent;

        // onReady also flips setupComplete; set here as a fallback in case the
        // SDK version doesn't fire onReady before mount resolves.
        if (!cancelled) {
          setSetupComplete(true);
        }
      } catch (err) {
        log('error', 'CC Checkout Flow', 'Flow init failed', err);
        if (!cancelled) {
          setSetupComplete(false);
        }
      }
    };

    const timer = setTimeout(() => {
      if (!cancelled) initFlow();
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (flowCardRef.current) {
        try { flowCardRef.current.unmount(); } catch { /* ignore */ }
        flowCardRef.current = null;
      }
      setSetupComplete(false);
      setGatewayErrors({});
      setFieldFocus(null);
      setCardType(null);
      setIsFlowCardComplete(false);
    };
  }, [isScriptReady, config.publicKey, config.paymentSession, config.environment, isFlowMode, log, resetKey, onCardTypeChange]);

  // ---------------------------------------------------------------------------
  // Frames mode initialization (unchanged from original)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (isFlowMode) return () => {};

    if (!isScriptReady || !window.Frames || !config.publicKey) {
      return () => {};
    }

    let cancelled = false;

    const timer = setTimeout(() => {
      if (cancelled) return;

      const cardNumberFrame = document.querySelector('.card-number-frame');
      const expiryFrame = document.querySelector('.expiry-date-frame');
      const cvvFrame = document.querySelector('.cvv-frame');

      if (!cardNumberFrame || !expiryFrame || !cvvFrame) {
        return;
      }

      log('info', 'CC Checkout Frames', 'creating hostedPaymentFields');

      window.Frames!.init({
        publicKey: config.publicKey,
        localization: {
          cardNumberPlaceholder: config.placeholders.cardNumber,
          cvvPlaceholder: config.placeholders.cvv,
          expiryMonthPlaceholder: config.placeholders.expiryMonth,
          expiryYearPlaceholder: config.placeholders.expiryYear
        },
        style: config.style || getDefaultCheckoutStyle(config.textColor || DEFAULT_TEXT_COLOR)
      });

      window.Frames!.addEventHandler(window.Frames!.Events.READY, () => {
        if (!cancelled) setSetupComplete(true);
      });

      window.Frames!.addEventHandler(window.Frames!.Events.FRAME_ACTIVATED, () => {
        if (!cancelled) setSetupComplete(true);
      });

      window.Frames!.addEventHandler(window.Frames!.Events.CARD_BIN_CHANGED, ({ scheme }) => {
        if (cancelled) return;
        setCardType(scheme || null);
        onCardTypeChange?.(scheme);
      });

      window.Frames!.addEventHandler(window.Frames!.Events.FRAME_FOCUS, ({ element }) => {
        if (cancelled) return;
        const key = CHECKOUT_INPUT_TYPES[camelCase(element) as keyof typeof CHECKOUT_INPUT_TYPES];
        setFieldFocus(key || null);
      });

      window.Frames!.addEventHandler(window.Frames!.Events.FRAME_BLUR, () => {
        if (!cancelled) setFieldFocus(null);
      });

      window.Frames!.addEventHandler(window.Frames!.Events.FRAME_VALIDATION_CHANGED, ({
        element,
        isValid
      }) => {
        if (cancelled) return;
        const camelCaseElement = camelCase(element);
        const errorKey = CHECKOUT_INPUT_TYPES[camelCaseElement as keyof typeof CHECKOUT_INPUT_TYPES];

        setGatewayErrors(prev => {
          const newErrors = { ...prev };
          if (!isValid) {
            newErrors[errorKey] = config.validationErrors?.[camelCaseElement]
              || `Invalid ${ camelCaseElement }`;
          } else {
            delete newErrors[errorKey];
          }
          return newErrors;
        });
      });
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timer);

      if (window.Frames) {
        try {
          window.Frames.removeAllEventHandlers(window.Frames.Events.READY);
          window.Frames.removeAllEventHandlers(window.Frames.Events.FRAME_ACTIVATED);
          window.Frames.removeAllEventHandlers(window.Frames.Events.CARD_BIN_CHANGED);
          window.Frames.removeAllEventHandlers(window.Frames.Events.FRAME_BLUR);
          window.Frames.removeAllEventHandlers(window.Frames.Events.FRAME_FOCUS);
          window.Frames.removeAllEventHandlers(window.Frames.Events.FRAME_VALIDATION_CHANGED);
        } catch {
          // Ignore cleanup errors
        }
      }

      setSetupComplete(false);
      setGatewayErrors({});
      setFieldFocus(null);
      setCardType(null);
    };
  }, [isScriptReady, config.publicKey, config.placeholders, config.style, config.validationErrors, config.textColor, onCardTypeChange, log, resetKey, isFlowMode]);

  // Notify parent of validation changes
  useEffect(() => {
    onValidationChange?.(gatewayErrors);
  }, [gatewayErrors, onValidationChange]);

  // Check for token expiration (Frames mode)
  useEffect(() => {
    if (isFlowMode) return;
    const tokenExpiredKey = Object.keys(gatewayErrors)
      .find((key) => gatewayErrors[key]?.toLowerCase() === TOKEN_EXPIRED_ERROR);

    if (tokenExpiredKey) {
      log('info', 'CC Checkout Frames', 'Token expired detected');
      setGatewayErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[tokenExpiredKey];
        return newErrors;
      });
    }
  }, [log, gatewayErrors, isFlowMode]);

  // Submit card — Flow tokenize or Frames submitCard
  const submitCard = useCallback(async (cardholderName: string, zip: string): Promise<Record<string, unknown>> => {
    if (isFlowMode) {
      if (!flowCardRef.current) {
        throw new Error('Flow card component not initialized');
      }
      log('info', 'CC Checkout Flow', 'Tokenizing card');
      try {
        const result = await flowCardRef.current.tokenize();
        return mapFlowTokenizeResult(result);
      } catch (err: unknown) {
        const flowErr = err as { code?: string };
        if (flowErr?.code === 'payment_session_expired') {
          log('info', 'CC Checkout Flow', 'Payment session expired');
        }
        throw err;
      }
    }

    if (!window.Frames) {
      throw new Error('Frames not initialized');
    }

    window.Frames.cardholder = {
      name: cardholderName,
      billingAddress: { zip }
    };

    log('info', 'CC Checkout Frames', 'Submitting card to gateway');
    return window.Frames.submitCard();
  }, [log, isFlowMode]);

  // Check if card is valid
  const isCardValid = useCallback((): boolean => {
    if (isFlowMode) return isFlowCardComplete;
    if (!window.Frames) return false;
    return window.Frames.isCardValid();
  }, [isFlowMode, isFlowCardComplete]);

  // Validate all fields (Frames only — Flow handles its own validation)
  const validateAllFields = useCallback((): boolean => {
    if (isFlowMode) return true;

    const invalidKeys: string[] = [];

    Object.entries(CHECKOUT_FRAME_CLASSES).forEach(([fieldName, className]) => {
      const el = document.getElementsByClassName(className)[0];
      if (el && !el.className.includes('frame--valid')) {
        invalidKeys.push(fieldName);
      }
    });

    if (invalidKeys.length === 0) return true;

    setGatewayErrors(prev => {
      const newErrors = { ...prev };
      invalidKeys.forEach((key) => {
        const errorKey = CHECKOUT_INPUT_TYPES[key as keyof typeof CHECKOUT_INPUT_TYPES];
        newErrors[errorKey] = config.validationErrors?.[key]
          || `Invalid ${ key }`;
      });
      return newErrors;
    });

    return false;
  }, [config.validationErrors, isFlowMode]);

  const reset = useCallback(() => {
    if (flowCardRef.current) {
      try { flowCardRef.current.unmount(); } catch { /* ignore */ }
      flowCardRef.current = null;
    }
    setSetupComplete(false);
    setGatewayErrors({});
    setIsFlowCardComplete(false);
    setResetKey(k => k + 1);
  }, []);

  const clearGatewayError = useCallback((key: string) => {
    setGatewayErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[key];
      return newErrors;
    });
  }, []);

  return {
    isScriptReady,
    isSetupComplete: setupComplete,
    scriptError,
    gatewayErrors,
    fieldFocus,
    cardType,
    isFlowMode,
    submitCard,
    isCardValid,
    validateAllFields,
    reset,
    clearGatewayError
  };
};
